import { TbCurrencyDollar, TbChartBar, TbStack2, TbChartLine, TbChartDonut, TbChartCandle } from 'react-icons/tb';
import { EFFICIENCY_BREAKDOWN_TOOLTIP } from '../../../shared/efficiencyBreakdownInfo';
import type {
    WidgetConfig,
    WidgetDomain,
    WidgetEditorCatalog,
    MetricCatalogEntry,
    ChartStyle,
} from './types';

export const INVOICING_STORAGE_KEY = 'reporting_invoicing_widgets';
export const INVOICING_LAYOUT_VERSION = 22;

export const INVOICING_COMPOSITION_METRICS = new Set([
    'flaggedFaultBreakdown',
    'stripeBillingMix',
]);

export const TOP_FLAGGED_VENDOR_TYPES_METRIC = 'topFlaggedVendorTypes';

/** Charts removed from the catalog. Saved layouts that still reference them are dropped. */
export function isRetiredInvoicingWidget(widget: WidgetConfig): boolean {
    return widget.metric === 'amountFlagBreakdown';
}

const AUTO_SUBMISSION_RATE_METRIC = 'autoSubmissionRate';
export const INVOICING_DEFAULT_LOCATION_LINE_CUTOFF = 10;

function isInvoicingTimeseriesBar(config: WidgetConfig): boolean {
    return config.widgetType === 'timeSeries' && (config.chartStyle ?? 'bars') === 'bars';
}

function isInvoicingStackedBar(config: WidgetConfig): boolean {
    return config.widgetType === 'timeSeries' && config.chartStyle === 'stacked';
}

function normalizeTimeseriesBarChart(config: WidgetConfig): Partial<WidgetConfig> {
    if (!isInvoicingTimeseriesBar(config)) return {};
    const patch: Partial<WidgetConfig> = { showComparisonBars: false };
    if (config.domain === 'invoicingLocation' || config.domain === 'invoicingVendor') {
        patch.chartStyle = 'line';
    }
    return patch;
}

function normalizeStackedBarChart(config: WidgetConfig): Partial<WidgetConfig> {
    if (!isInvoicingStackedBar(config)) return {};
    if (config.domain === 'invoicingLocation' || config.domain === 'invoicingVendor') {
        return { chartStyle: 'histogram', sort: config.sort ?? 'desc' };
    }
    return {};
}

/** $ metrics with signed credits — donut charts cannot represent negative slices. */
export const INVOICING_METRICS_NO_DONUT = new Set([
    'invoiceValue',
    'invoiceFlagsValue',
    'avgCostVariance',
    'totalCostVariance',
    'avgDeliveryVarianceDays',
]);

/** Flag/rate metrics are not present on byVendor segments. */
const VENDOR_UNSUPPORTED_CHART_METRICS = new Set([
    'invoiceFlags',
    'invoiceFlagsValue',
    'autoSubmissionRate',
]);

export function invoicingMetricAllowsDonut(metric: string): boolean {
    return !INVOICING_METRICS_NO_DONUT.has(metric) && metric !== AUTO_SUBMISSION_RATE_METRIC;
}

function allowedAutoSubmissionRateChartStyles(domain: WidgetDomain): ChartStyle[] {
    return (domain === 'invoicingLocation' || domain === 'invoicingVendor')
        ? ['line', 'histogram']
        : ['line', 'bars'];
}

function normalizeAutoSubmissionRateChart(config: WidgetConfig): Partial<WidgetConfig> {
    if (config.metric !== AUTO_SUBMISSION_RATE_METRIC || config.widgetType === 'keyMetric') return {};
    const style = config.chartStyle ?? 'bars';
    if (allowedAutoSubmissionRateChartStyles(config.domain).includes(style)) return {};
    return { chartStyle: 'line' };
}

const LEGACY_INVOICING_METRIC_ALIASES: Record<string, string> = {
    totalInvoices: 'processedInvoiceCount',
    totalUploads: 'processedInvoiceCount',
    uploadCount: 'invoiceCount',
};

function migrateInvoicingMetric(metric: string): string {
    if (metric in LEGACY_INVOICING_METRIC_ALIASES) {
        return LEGACY_INVOICING_METRIC_ALIASES[metric];
    }
    return metric;
}

export function sanitizeInvoicingWidget(widget: WidgetConfig): WidgetConfig {
    let next = {
        ...widget,
        metric: migrateInvoicingMetric(widget.metric),
    };
    if (next.chartStyle === 'donut' && INVOICING_METRICS_NO_DONUT.has(next.metric)) {
        next = { ...next, chartStyle: 'line' };
    }
    const rateNorm = normalizeAutoSubmissionRateChart(next);
    if (rateNorm.chartStyle) {
        next = { ...next, ...rateNorm };
    }
    const barNorm = normalizeTimeseriesBarChart(next);
    if (Object.keys(barNorm).length > 0) {
        next = { ...next, ...barNorm };
    }
    const stackedNorm = normalizeStackedBarChart(next);
    if (Object.keys(stackedNorm).length > 0) {
        next = { ...next, ...stackedNorm };
    }
    if (next.showComparisonBars === undefined) {
        next = { ...next, showComparisonBars: false };
    }
    if (
        (next.domain === 'invoicingLocation' || next.domain === 'invoicingVendor')
        && next.chartStyle === 'histogram'
        && !next.sort
    ) {
        next = { ...next, sort: 'desc' };
    }
    if (
        next.widgetType === 'timeSeries'
        && (next.domain === 'invoicingLocation' || next.domain === 'invoicingVendor')
        && next.chartStyle === 'line'
        && !next.showAllRows
        && (next.cutoff === undefined || next.cutoff < 1)
    ) {
        next = { ...next, cutoff: INVOICING_DEFAULT_LOCATION_LINE_CUTOFF };
    }
    if ('excludeEvs' in next) {
        const { excludeEvs: _omit, ...stripped } = next;
        next = { ...stripped, dashboard: 'invoicing' as const };
    } else if (next.dashboard !== 'invoicing') {
        next = { ...next, dashboard: 'invoicing' };
    }
    if (
        next.domain === 'invoicingVendor'
        && next.widgetType === 'timeSeries'
        && VENDOR_UNSUPPORTED_CHART_METRICS.has(next.metric)
    ) {
        next = { ...next, metric: 'invoiceCount', format: 'number', stat: 'total' };
    }
    return syncInvoicingWidgetLabels(next);
}

const INVOICING_PRIOR_BAR_CHART_STYLES = new Set<ChartStyle>(['histogram']);

function invoicingShowPriorBars(config: WidgetConfig): boolean {
    if (config.widgetType !== 'timeSeries') return false;
    const style = config.chartStyle ?? 'bars';
    if (INVOICING_PRIOR_BAR_CHART_STYLES.has(style)) return true;
    return style === 'stacked' && config.domain === 'invoicingTotal';
}

/** Summary (keyMetric) metrics — keys match InvoicingSummaryResponse fields. */
const INVOICING_SUMMARY_METRICS: MetricCatalogEntry[] = [
    {
        value: 'processedInvoiceCount',
        label: 'Submitted Invoices',
        format: 'number',
        stat: 'total',
        tooltip: 'Invoices that reached accounting (auto-submitted + manually submitted). Excludes still-flagged only — archive status does not affect inclusion. All invoice types count.',
    },
    {
        value: 'totalInvoicesValue',
        label: 'Submitted Invoices',
        format: 'currency',
        stat: 'total',
        tooltip: 'Dollar value of invoices that reached accounting (auto-submitted + manually submitted). Excludes still-flagged only — archive status does not affect inclusion. Types: Received, Credit, Return Slip ($0), and Sublet.',
    },
    {
        value: 'avgPartCount',
        label: 'Avg. Parts / Invoice',
        format: 'number',
        stat: 'average',
        tooltip: 'Average parts per invoice for auto-submitted and manually submitted only. Excludes still-flagged. Archive status does not affect inclusion. All invoice types.',
    },
    {
        value: 'medianPartCount',
        label: 'Median Parts / Invoice',
        format: 'number',
        stat: 'median',
        tooltip: 'Median parts per invoice for auto-submitted and manually submitted only. Excludes still-flagged. Archive status does not affect inclusion. All invoice types.',
    },
    {
        value: 'avgInvoiceValue',
        label: 'Avg. Invoice Value',
        format: 'currency',
        stat: 'average',
        tooltip: 'Average order total for Received and Sublet invoices only. Auto-submitted and manually submitted; excludes still-flagged. Archive status does not affect inclusion. Credits and return slips are not included.',
    },
    {
        value: 'medianInvoiceValue',
        label: 'Median Invoice Value',
        format: 'currency',
        stat: 'median',
        tooltip: 'Median order total for Received and Sublet invoices only. Auto-submitted and manually submitted; excludes still-flagged. Archive status does not affect inclusion. Credits and return slips are not included.',
    },
    {
        value: 'invoiceFlags',
        label: 'Invoice Flags',
        format: 'number',
        stat: 'total',
        tooltip: 'Still-flagged invoices in review (excluding archived) plus manually submitted ones. All invoice types count toward the number.',
    },
    {
        value: 'invoiceFlagsValue',
        label: 'Invoice Flags ($)',
        format: 'currency',
        stat: 'total',
        tooltip: 'Dollar value of still-flagged (non-archived) and manually submitted invoices. Return slips count as $0.',
    },
    {
        value: 'autoSubmissionRate',
        label: 'Auto Submission Rate',
        format: 'percent',
        stat: 'average',
        tooltip: 'Auto-submitted ÷ (auto-submitted + manually submitted) × 100. Excludes duplicate uploads and still-flagged invoices. Archive status does not affect inclusion.',
    },
    {
        value: 'creditsCount',
        label: 'Credits',
        format: 'number',
        stat: 'total',
        tooltip: 'Count of Credit (return) invoices that reached accounting. Excludes still-flagged only. Archive status does not affect inclusion.',
    },
    {
        value: 'creditsValue',
        label: 'Credits ($)',
        format: 'currency',
        stat: 'total',
        tooltip: 'Total dollar value of Credit (return) invoices that reached accounting. Excludes still-flagged only. Archive status does not affect inclusion.',
    },
    {
        value: 'totalCostVariance',
        label: 'Total Cost Variance',
        format: 'currency',
        stat: 'total',
        tooltip: 'Sum of invoiced cost minus PO buy across matched parts on processed invoices. Positive = invoice higher than PO (overcharge). Rolled up from byVendor.',
    },
    {
        value: 'avgCostVariance',
        label: 'Avg. Cost Variance',
        format: 'currency',
        stat: 'average',
        tooltip: 'Matched-part-weighted average of invoiced cost minus PO buy. Positive = invoice higher than PO (overcharge). Rolled up from byVendor.',
    },
    {
        value: 'avgDeliveryVarianceDays',
        label: 'Avg. Delivery Variance',
        format: 'number',
        stat: 'average',
        tooltip: 'Part-weighted average of invoice day minus promised delivery day across vendors (Σ delivery days / Σ parts with samples). Positive = arrived late. Null when no delivery samples.',
    },
];

/** Chart (time-series / distribution) metrics. */
const INVOICING_CHART_METRICS: MetricCatalogEntry[] = [
    {
        value: 'invoiceCount',
        label: 'Submitted Invoices',
        format: 'number',
        stat: 'total',
        tooltip: 'Invoice count per period — same rules as the Submitted Invoices summary card. Auto-submitted and manually submitted only; excludes still-flagged. Archive status does not affect inclusion. All invoice types.',
    },
    {
        value: 'invoiceValue',
        label: 'Submitted Invoices',
        format: 'currency',
        stat: 'total',
        tooltip: 'Invoice dollar value per period — same rules as the Submitted Invoices summary card. Auto-submitted and manually submitted only; excludes still-flagged. Archive status does not affect inclusion. Types: Received, Credit, Return Slip ($0), Sublet.',
    },
    {
        value: 'invoiceFlags',
        label: 'Invoice Flags',
        format: 'number',
        stat: 'total',
        tooltip: 'Still-flagged (non-archived) and manually submitted invoices per period. All invoice types.',
    },
    {
        value: 'invoiceFlagsValue',
        label: 'Invoice Flags ($)',
        format: 'currency',
        stat: 'total',
        tooltip: 'Dollar value of still-flagged (non-archived) and manually submitted invoices per period. Return slips count as $0.',
    },
    {
        value: 'autoSubmissionRate',
        label: 'Auto Submission Rate',
        format: 'percent',
        stat: 'average',
        tooltip: 'Auto-submitted ÷ (auto-submitted + manually submitted) × 100 per period. Excludes duplicates and still-flagged. Archive status does not affect inclusion.',
    },
    {
        value: 'avgCostVariance',
        label: 'Avg. Cost Variance',
        format: 'currency',
        stat: 'average',
        tooltip: 'Average invoiced cost minus PO buy per matched part. Positive = invoice higher than PO (overcharge). Available under Vendor breakdown.',
    },
    {
        value: 'totalCostVariance',
        label: 'Total Cost Variance',
        format: 'currency',
        stat: 'total',
        tooltip: 'Sum of invoiced cost minus PO buy for matched parts. Positive = invoice higher than PO (overcharge). Available under Vendor breakdown.',
    },
    {
        value: 'avgDeliveryVarianceDays',
        label: 'Avg. Delivery Variance',
        format: 'number',
        stat: 'average',
        tooltip: 'Average invoice day minus promised delivery day. Positive = arrived late. Null when a vendor has no delivery samples. Available under Vendor breakdown.',
    },
    {
        value: 'matchedPartCount',
        label: 'Matched Parts',
        format: 'number',
        stat: 'total',
        tooltip: 'Count of matched order parts included in cost variance. Available under Vendor breakdown.',
    },
    {
        value: 'flaggedFaultBreakdown',
        label: 'Efficiency Breakdown',
        format: 'number',
        stat: 'total',
        tooltip: EFFICIENCY_BREAKDOWN_TOOLTIP,
    },
    {
        value: 'stripeBillingMix',
        label: 'Billable Invoice Breakdown',
        format: 'number',
        stat: 'total',
        tooltip: 'Invoices in the selected dates that can be billed: submitted or manually submitted, and still flagged, each split by archived or not. Uses the same date, location, vendor, and invoice-type filters as the other charts on this tab.',
    },
    {
        value: TOP_FLAGGED_VENDOR_TYPES_METRIC,
        label: 'Most Flagged Vendor/Type Slices',
        format: 'percent',
        stat: 'total',
        tooltip: 'Vendor and invoice-type pairs with at least 10 invoices in this period, ranked by worst auto-submit rate. That rate uses the same rules as the summary card. Click a row to open those invoices.',
    },
];

const VENDOR_ONLY_CHART_METRICS = new Set([
    'avgCostVariance',
    'totalCostVariance',
    'avgDeliveryVarianceDays',
    'matchedPartCount',
]);

function invoicingCatalogEntry(metric: string): MetricCatalogEntry | undefined {
    return [...INVOICING_SUMMARY_METRICS, ...INVOICING_CHART_METRICS].find(m => m.value === metric);
}

function syncInvoicingWidgetLabels(widget: WidgetConfig): WidgetConfig {
    const entry = invoicingCatalogEntry(widget.metric);
    if (!entry) return widget;
    const byBreakdownSuffix = widget.widgetType === 'timeSeries'
        ? (widget.domain === 'invoicingLocation'
            ? ' by Location'
            : widget.domain === 'invoicingVendor'
                ? ' by Vendor'
                : '')
        : '';
    return {
        ...widget,
        title: `${entry.label}${byBreakdownSuffix}`,
        description: entry.tooltip,
    };
}

const INVOICING_DOMAINS: { value: WidgetDomain; label: string }[] = [
    { value: 'invoicingTotal', label: 'Total' },
    { value: 'invoicingLocation', label: 'Location' },
    { value: 'invoicingVendor', label: 'Vendor' },
];

const INVOICING_VARIANT_GROUPS = [
    { label: 'Submitted Invoices', variants: [{ value: 'processedInvoiceCount', label: 'Total' }, { value: 'totalInvoicesValue', label: 'Value' }] },
    { label: 'Submitted Invoices', variants: [{ value: 'invoiceCount', label: 'Total' }, { value: 'invoiceValue', label: 'Value' }] },
    { label: 'Invoice Size (Parts)', variants: [{ value: 'avgPartCount', label: 'Avg.' }, { value: 'medianPartCount', label: 'Median' }] },
    { label: 'Invoice Size ($)', variants: [{ value: 'avgInvoiceValue', label: 'Avg.' }, { value: 'medianInvoiceValue', label: 'Median' }] },
    { label: 'Invoice Flags', variants: [{ value: 'invoiceFlags', label: '#' }, { value: 'invoiceFlagsValue', label: '$' }] },
    { label: 'Credits', variants: [{ value: 'creditsCount', label: '#' }, { value: 'creditsValue', label: '$' }] },
    { label: 'Cost Variance', variants: [{ value: 'totalCostVariance', label: 'Total' }, { value: 'avgCostVariance', label: 'Avg.' }] },
];

function generateId() {
    return `invwidget-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function buildKeyMetric(metricValue: string): WidgetConfig {
    const entry = INVOICING_SUMMARY_METRICS.find(m => m.value === metricValue)!;
    return {
        id: generateId(),
        title: entry.label,
        description: entry.tooltip,
        dashboard: 'invoicing',
        widgetType: 'keyMetric',
        domain: 'invoicingTotal',
        metric: entry.value,
        stat: entry.stat,
        format: entry.format,
        dataSource: 'summary',
        gridSpan: 1,
        comparison: true,
    };
}

function buildChart(metricValue: string, chartStyle: ChartStyle, domain: WidgetDomain = 'invoicingTotal', gridSpan = 2): WidgetConfig {
    const entry = INVOICING_CHART_METRICS.find(m => m.value === metricValue)!;
    return {
        id: generateId(),
        title: entry.label,
        description: entry.tooltip,
        dashboard: 'invoicing',
        widgetType: 'timeSeries',
        domain,
        metric: entry.value,
        stat: entry.stat,
        format: entry.format,
        dataSource: 'timeseries',
        gridSpan,
        comparison: true,
        chartStyle,
    };
}

/**
 * Default layout: the 5 spec summary cards, one full-width chart, then a
 * donut + line side by side.
 */
export function getDefaultInvoicingWidgets(): WidgetConfig[] {
    return [
        // 5 summary cards (Submitted Invoices, Invoice Size, Invoice Flags, Auto Submission Rate, Credits)
        buildKeyMetric('processedInvoiceCount'),
        buildKeyMetric('avgInvoiceValue'),
        buildKeyMetric('invoiceFlags'),
        buildKeyMetric('autoSubmissionRate'),
        buildKeyMetric('creditsValue'),
        // Big chart: invoice volume over time, stacked by invoice type
        buildChart('invoiceCount', 'stacked', 'invoicingTotal', 4),
        // Smaller charts: flag-type donut + auto submission rate trend line
        buildChart('invoiceFlags', 'donut', 'invoicingTotal', 2),
        buildChart('flaggedFaultBreakdown', 'donut', 'invoicingTotal', 2),
        buildChart('stripeBillingMix', 'donut', 'invoicingTotal', 2),
        {
            ...buildChart(TOP_FLAGGED_VENDOR_TYPES_METRIC, 'donut', 'invoicingTotal', 2),
            dataSource: 'summary',
        },
        buildChart('autoSubmissionRate', 'line', 'invoicingTotal', 4),
        {
            ...buildChart('autoSubmissionRate', 'histogram', 'invoicingLocation', 4),
            title: 'Auto Submission Rate by Location',
            sort: 'desc',
        },
    ];
}

const CHART_STYLE_CARDS: { value: ChartStyle; label: string; icon: React.ElementType }[] = [
    { value: 'bars', label: 'Time Series Bar', icon: TbChartCandle as React.ElementType },
    { value: 'stacked', label: 'Stacked Bar', icon: TbStack2 as React.ElementType },
    { value: 'line', label: 'Line Chart', icon: TbChartLine as React.ElementType },
    { value: 'donut', label: 'Donut Chart', icon: TbChartDonut as React.ElementType },
    { value: 'histogram', label: 'Bar Chart', icon: TbChartBar as React.ElementType },
];

const INVOICING_WIDGET_TYPES: { value: string; label: string; icon: React.ElementType }[] = [
    { value: 'keyMetric', label: 'Summary Card', icon: TbCurrencyDollar as React.ElementType },
    ...CHART_STYLE_CARDS,
];

function invoicingChartMetricsFor(config: WidgetConfig): MetricCatalogEntry[] {
    if (INVOICING_COMPOSITION_METRICS.has(config.metric) || config.metric === TOP_FLAGGED_VENDOR_TYPES_METRIC) {
        return INVOICING_CHART_METRICS.filter(m => m.value === config.metric);
    }
    const metrics = config.chartStyle === 'donut'
        ? INVOICING_CHART_METRICS.filter(m => invoicingMetricAllowsDonut(m.value))
        : INVOICING_CHART_METRICS;
    return metrics.filter(m => !INVOICING_COMPOSITION_METRICS.has(m.value) && m.value !== TOP_FLAGGED_VENDOR_TYPES_METRIC);
}

function defaultDonutChartMetric(): MetricCatalogEntry {
    return INVOICING_CHART_METRICS.find(m => invoicingMetricAllowsDonut(m.value))!;
}

export const INVOICING_CATALOG: WidgetEditorCatalog = {
    widgetTypes: INVOICING_WIDGET_TYPES,
    widgetTypesFor: (config) => {
        let types = INVOICING_WIDGET_TYPES;
        if (INVOICING_COMPOSITION_METRICS.has(config.metric) || config.metric === TOP_FLAGGED_VENDOR_TYPES_METRIC) {
            return types.filter(t => t.value === 'donut');
        }
        if (config.widgetType !== 'keyMetric' && !invoicingMetricAllowsDonut(config.metric)) {
            types = types.filter(t => t.value !== 'donut');
        }
        if (config.metric === AUTO_SUBMISSION_RATE_METRIC && config.widgetType !== 'keyMetric') {
            const allowed = new Set<string>(['keyMetric', ...allowedAutoSubmissionRateChartStyles(config.domain)]);
            types = types.filter(t => allowed.has(t.value));
        }
        if (
            (config.domain === 'invoicingLocation' || config.domain === 'invoicingVendor')
            && config.widgetType !== 'keyMetric'
        ) {
            types = types.filter(t => t.value !== 'stacked' && t.value !== 'bars');
        }
        return types;
    },
    domains: INVOICING_DOMAINS,
    domainsFor: (config) => (
        (isInvoicingTimeseriesBar(config) || isInvoicingStackedBar(config) || config.metric === TOP_FLAGGED_VENDOR_TYPES_METRIC)
            ? INVOICING_DOMAINS.filter((d) => d.value === 'invoicingTotal')
            : INVOICING_DOMAINS
    ),
    metricsFor: (config) => {
        if (config.widgetType === 'keyMetric') return INVOICING_SUMMARY_METRICS;
        const charts = invoicingChartMetricsFor(config);
        if (config.domain === 'invoicingVendor') {
            return charts.filter((m) => !VENDOR_UNSUPPORTED_CHART_METRICS.has(m.value));
        }
        return charts.filter((m) => !VENDOR_ONLY_CHART_METRICS.has(m.value));
    },
    variantGroups: INVOICING_VARIANT_GROUPS,
    selectedCardId: (config) => (config.widgetType === 'keyMetric' ? 'keyMetric' : (config.chartStyle ?? 'bars')),
    applyCardId: (cardId, config) => {
        if (cardId === 'keyMetric') {
            return { widgetType: 'keyMetric', dataSource: 'summary', gridSpan: 1, chartStyle: undefined };
        }
        if (cardId === 'donut' && !invoicingMetricAllowsDonut(config.metric)) {
            const fallback = defaultDonutChartMetric();
            return {
                widgetType: 'timeSeries',
                dataSource: 'timeseries',
                gridSpan: 2,
                chartStyle: 'donut',
                metric: fallback.value,
                format: fallback.format,
                stat: fallback.stat,
            };
        }
        let chartStyle = cardId as ChartStyle;
        if (config.metric === AUTO_SUBMISSION_RATE_METRIC) {
            const allowed = allowedAutoSubmissionRateChartStyles(config.domain);
            if (!allowed.includes(chartStyle)) {
                chartStyle = 'line';
            }
        }
        const partial: Partial<WidgetConfig> = {
            widgetType: 'timeSeries',
            dataSource: 'timeseries',
            gridSpan: 2,
            chartStyle,
        };
        if (chartStyle === 'bars') {
            partial.domain = 'invoicingTotal';
            partial.showComparisonBars = false;
        }
        if (chartStyle === 'stacked') {
            partial.domain = 'invoicingTotal';
        }
        return partial;
    },
    applyMetric: (metric, config) => {
        if (config.chartStyle === 'donut' && !invoicingMetricAllowsDonut(metric)) {
            const fallback = defaultDonutChartMetric();
            return { metric: fallback.value, format: fallback.format, stat: fallback.stat };
        }
        if (metric === AUTO_SUBMISSION_RATE_METRIC) {
            const entry = INVOICING_CHART_METRICS.find(m => m.value === metric)!;
            return {
                metric,
                format: entry.format,
                stat: entry.stat,
                ...normalizeAutoSubmissionRateChart({ ...config, metric }),
            };
        }
        if (VENDOR_ONLY_CHART_METRICS.has(metric) && config.domain !== 'invoicingVendor') {
            return { domain: 'invoicingVendor' as WidgetDomain, metric };
        }
        return {};
    },
    applyDomain: (domain, config) => {
        const patch: Partial<WidgetConfig> = {
            ...normalizeAutoSubmissionRateChart({ ...config, domain }),
            ...normalizeTimeseriesBarChart({ ...config, domain }),
            ...normalizeStackedBarChart({ ...config, domain }),
        };
        if (
            config.widgetType === 'timeSeries'
            && (domain === 'invoicingLocation' || domain === 'invoicingVendor')
            && (config.chartStyle === 'line' || patch.chartStyle === 'line')
            && !config.showAllRows
            && (config.cutoff === undefined || config.cutoff < 1)
        ) {
            patch.cutoff = INVOICING_DEFAULT_LOCATION_LINE_CUTOFF;
        }
        if (domain !== 'invoicingVendor' && VENDOR_ONLY_CHART_METRICS.has(config.metric)) {
            patch.metric = 'invoiceCount';
        }
        if (domain === 'invoicingVendor' && VENDOR_UNSUPPORTED_CHART_METRICS.has(config.metric)) {
            patch.metric = 'invoiceCount';
            patch.format = 'number';
            patch.stat = 'total';
        }
        return patch;
    },
    showPriorFor: invoicingShowPriorBars,
    showSortFor: (config) =>
        config.widgetType === 'timeSeries'
        && (config.domain === 'invoicingLocation' || config.domain === 'invoicingVendor')
        && config.chartStyle === 'histogram',
    showLocationLimitFor: (config) =>
        config.widgetType === 'timeSeries'
        && (config.domain === 'invoicingLocation' || config.domain === 'invoicingVendor')
        && config.chartStyle === 'line',
    defaultConfig: () => buildChart('invoiceCount', 'stacked'),
};
